import dayjs from 'dayjs';
import { CaseReducer, PayloadAction, createSlice } from '@reduxjs/toolkit';

import { loading as commonLoading } from 'store/common';
import { post } from 'api/requests';

import { getCalendar } from './calendarSlice';
import { getDistribution } from './distributionSlice';

import { DATE_FORMAT, TIME_FORMAT } from 'consts';

import { selectCalendarParams } from 'pages/Timesheet/TimesheetEngNetworks/selectors';

import { IReducer, AppThunk } from 'store/types';
import {
  IDraggable,
  IDragSource,
  IDragData,
  IPostMeetingData,
} from 'pages/Timesheet/TimesheetEngNetworks/types';
import { IDraggableCard, IParamsReq } from 'types';

const FORWARD_TIMEOUT = 2000; // ms

const initialState: IDraggable = {
  loading: false,
  draggable: false,
  current: {} as IDraggableCard,
  data: {
    dropId: '',
    dragId: '',
    dragParentId: '',
    dropParentId: '',
    dragSource: '' as IDragSource,
    dropSource: '' as IDragSource,
    dragTaskMinutes: 0,
  },
  isDragForwarding: false,
  forwardingStepTime: 0,
};

const reset: CaseReducer<IDraggable> = () => initialState;

const draggable: CaseReducer<IDraggable, PayloadAction<boolean>> = (state, action) => {
  state.draggable = action.payload;
};

const data: CaseReducer<IDraggable, PayloadAction<IDragData>> = (state, action) => {
  state.data = action.payload;
};

const loading: IReducer<IDraggable, boolean> = commonLoading;

const current: CaseReducer<IDraggable, PayloadAction<IDraggableCard>> = (state, action) => {
  state.current = action.payload;
};

const isDragForwarding: CaseReducer<IDraggable, PayloadAction<boolean>> = (state, action) => {
  state.isDragForwarding = action.payload;
};

const forwardingStepTime: CaseReducer<IDraggable, PayloadAction> = state => {
  const now = Date.now();
  if (state.forwardingStepTime + FORWARD_TIMEOUT < now) {
    state.forwardingStepTime = now;
  }
};

export const draggableSlice = createSlice({
  name: 'draggable',
  initialState,
  reducers: {
    draggable,
    current,
    loading,
    data,
    reset,
    isDragForwarding,
    forwardingStepTime,
  },
});

const {
  draggable: setDraggable,
  current: setDragCurrent,
  reset: resetState,
  data: setDragData,
  loading: setLoading,
  isDragForwarding: setIsDragForwarding,
  forwardingStepTime: setForwardingStepTime,
} = draggableSlice.actions;

export const setData =
  (props: Partial<IDragData>): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { engineeringCalendar } = timesheet;

    dispatch(setDragData({ ...engineeringCalendar.drag.data, ...props }));
  };

export const setCurrent =
  (props: Partial<IDraggableCard>): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { engineeringCalendar } = timesheet;

    dispatch(setDragCurrent({ ...engineeringCalendar.drag.current, ...props }));
  };

const formatActualTime = (date: string, time: string) =>
  dayjs.utc(`${date} ${time}`, `${DATE_FORMAT} ${TIME_FORMAT}`).toISOString();

export const arrangeMeeting =
  (search: IParamsReq): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { timesheet } = state;
    const { engineeringCalendar } = timesheet;
    const { current, data: dropData } = engineeringCalendar.drag;

    const data: IPostMeetingData = {
      id: current.id,
      actualTime: formatActualTime(dropData.dropParentId, current.startTime),
    };

    const { start_date } = selectCalendarParams(state);

    dispatch(setLoading(true));

    post(`/api/inspection_requests/meetings/${current.id}/transition/`, data)
      .then(() => {
        dispatch(resetState());
        dispatch(
          getCalendar({
            start_date: start_date.split('.').reverse().join('-'),
          }),
        );
        dispatch(getDistribution(search));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const arrangeAppointment =
  (search: IParamsReq): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { timesheet } = state;
    const { engineeringCalendar } = timesheet;
    const { current, data: dropData } = engineeringCalendar.drag;
    const { endTime, startTime, pk } = current;

    const data: IPostMeetingData = {
      startTime: startTime.split('.').reverse().join('-'),
      endTime: endTime.split('.').reverse().join('-'),
      date: dropData.dropParentId,
      appointment: pk,
    };

    const { start_date } = selectCalendarParams(state);

    dispatch(setLoading(true));

    post('/api/v2/timesheet/meeting-engineer/', data)
      .then(() => {
        dispatch(resetState());
        dispatch(
          getCalendar({
            start_date: start_date.split('.').reverse().join('-'),
          }),
        );
        dispatch(getDistribution(search));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export { setDraggable, resetState, setIsDragForwarding, setForwardingStepTime };
